// Canvas 2D renderer for the 3D market-cap heatmap (ported from radar's
// heatmap-scene). Isometric prisms on a treemap, coloured by 24h price change.
// Colours come from the terminal's CSS variables, read at draw time.

import type { CapBlock, HeatTile } from "./heatmap-layout";

type Vertex = [number, number, number];
type Point = { x: number; y: number; depth: number };
type Wave = { x: number; z: number; born: number };
export type Rgb = [number, number, number];

export const DEFAULT_CAMERA = { yaw: -0.48, pitch: 0.72, zoom: 1 };
export type Camera = typeof DEFAULT_CAMERA;
export type SceneSettings = {
  camera: Camera;
  selected: string;
  motion: boolean;
  relief: boolean;
};

/** A 24h move of this size or more gets the full up/down colour. */
export const FULL_SCALE_CHANGE = 0.05;

export function rippleHeight(x: number, z: number, time: number, waves: Wave[]): number {
  let height = 0;
  for (const wave of waves) {
    const age = time - wave.born;
    if (age < 0 || age > 3) continue;
    const distance = Math.hypot(x - wave.x, z - wave.z);
    const front = distance - age * 290;
    height += Math.sin(front * 0.032) * Math.exp((-front * front) / 26000) * Math.exp(-age * 1.25) * 42;
  }
  return Math.max(-48, Math.min(48, height));
}

/** Parses #rgb, #rrggbb and rgb()/rgba() strings; null for anything else. */
export function parseColor(value: string): Rgb | null {
  const text = value.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(text)?.[1];
  if (hex) {
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join("") : hex;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as Rgb;
  }
  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(text);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

export const mix = (a: Rgb, b: Rgb, t: number): Rgb => [0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * t) as Rgb;
export const css = (c: Rgb, alpha = 1): string =>
  `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${alpha})`;

export interface Palette {
  paper: Rgb;
  ink: Rgb;
  muted: Rgb;
  line: Rgb;
  lineStrong: Rgb;
  signal: Rgb;
  signalFill: Rgb;
  up: Rgb;
  down: Rgb;
}

const FALLBACK: Palette = {
  paper: [0, 0, 0],
  ink: [240, 239, 233],
  muted: [143, 143, 134],
  line: [38, 38, 34],
  lineStrong: [77, 77, 69],
  signal: [167, 139, 250],
  signalFill: [91, 33, 182],
  up: [0, 200, 5],
  down: [255, 72, 56],
};

const VARIABLES: Record<keyof Palette, string> = {
  paper: "--paper",
  ink: "--ink",
  muted: "--ink-muted",
  line: "--line",
  lineStrong: "--line-strong",
  signal: "--signal",
  signalFill: "--signal-fill",
  up: "--up",
  down: "--down",
};

export function readPalette(element: Element): Palette {
  const style = getComputedStyle(element);
  const palette = { ...FALLBACK };
  for (const key of Object.keys(VARIABLES) as (keyof Palette)[]) {
    palette[key] = parseColor(style.getPropertyValue(VARIABLES[key])) ?? FALLBACK[key];
  }
  return palette;
}

/**
 * Tile colour for a 24h change: grey when flat, blending to --up / --down at
 * ±FULL_SCALE_CHANGE. Unknown change (null) is a darker neutral grey.
 */
export function changeColor(change: number | null, palette: Palette): Rgb {
  if (change === null || !Number.isFinite(change)) return palette.lineStrong;
  const t = Math.max(-1, Math.min(1, change / FULL_SCALE_CHANGE));
  const strength = Math.sqrt(Math.abs(t));
  return mix(palette.muted, t >= 0 ? palette.up : palette.down, strength);
}

function contains(polygon: Point[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

export type HeatmapScene = NonNullable<ReturnType<typeof createHeatmapScene>>;

/** One prism per block: its unit faces (height applied at draw time) and centre. */
export interface PrismGeometry {
  block: CapBlock<HeatTile>;
  /** sqrt(cap / largest cap), 0..1: drives altitude and brightness. */
  intensity: number;
  cx: number;
  cz: number;
  faces: Vertex[][];
}

/** Builds the cached prism geometry for a layout; recomputed only when the data changes. */
export function buildGeometry(blocks: readonly CapBlock<HeatTile>[]): PrismGeometry[] {
  const largest = Math.max(0, ...blocks.map((block) => block.token.mcap));
  if (!(largest > 0)) return [];
  return blocks.map((block) => {
    const intensity = Math.sqrt(block.token.mcap / largest);
    const cx = block.x + block.width / 2,
      cz = block.z + block.depth / 2;
    // Separate the blocks without changing their cap-proportional footprints.
    const x = block.x + cx * 0.055,
      z = block.z + cz * 0.055;
    const gap = Math.min(3, block.width * 0.05, block.depth * 0.05);
    const x0 = x + gap,
      x1 = x + block.width - gap,
      z0 = z + gap,
      z1 = z + block.depth - gap;
    const faces: Vertex[][] = [
      [[x0, 1, z0], [x1, 1, z0], [x1, 1, z1], [x0, 1, z1]],
      [[x0, 0, z0], [x1, 0, z0], [x1, 1, z0], [x0, 1, z0]],
      [[x1, 0, z0], [x1, 0, z1], [x1, 1, z1], [x1, 1, z0]],
      [[x1, 0, z1], [x0, 0, z1], [x0, 1, z1], [x1, 1, z1]],
      [[x0, 0, z1], [x0, 0, z0], [x0, 1, z0], [x0, 1, z1]],
    ];
    return { block, intensity, cx: cx * 1.055, cz: cz * 1.055, faces };
  });
}

// One bounded animation loop. Geometry is cached; camera/pointer updates do not
// rebuild React state or allocate another renderer, and a data refresh swaps
// the geometry in place (setBlocks) so the camera, ripples and observers live on.
// No GPU/WebGL requirement.
export function createHeatmapScene(canvas: HTMLCanvasElement, blocks: readonly CapBlock<HeatTile>[], initial: SceneSettings) {
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) return null;
  const ctx = context;
  let settings = initial;
  let width = 0,
    height = 0,
    dpr = 1,
    frame = 0,
    lastFrame = 0,
    time = 0;
  let disposed = false,
    visible = true,
    dirty = true,
    hover = "",
    lastRipple = -1;
  let waves: Wave[] = [];
  let hits: { polygon: Point[]; block: CapBlock<HeatTile> }[] = [];
  const preference = matchMedia("(prefers-reduced-motion: reduce)");
  const animated = () => settings.motion && !preference.matches;
  let geometry = buildGeometry(blocks);

  function draw(): void {
    if (!width || !height) return;
    const palette = readPalette(canvas);
    const { camera } = settings;
    // Narrow (phone) canvases: leave room for the perspective-enlarged near
    // corner and centre the platform vertically.
    const narrow = width < 560;
    const scale = Math.min(width / (narrow ? 1600 : 1430), height / 1100) * camera.zoom;
    const anchorY = narrow ? 0.5 : 0.57;
    const cy = Math.cos(camera.yaw),
      sy = Math.sin(camera.yaw);
    const cp = Math.cos(camera.pitch),
      sp = Math.sin(camera.pitch);
    const project = ([x, y, z]: Vertex): Point => {
      const rx = x * cy - z * sy,
        rz = x * sy + z * cy;
      const depth = rz * cp + y * sp;
      const perspective = 2100 / (2100 - depth);
      return { x: width / 2 + rx * scale * perspective, y: height * anchorY +(rz * sp - y * cp) * scale * perspective, depth };
    };
    const trace = (points: Point[]) => {
      ctx.beginPath();
      points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
    };
    const lift = (x: number, z: number) =>
      settings.relief ? 24 + Math.sin(x * 0.009 + time * 0.75) * Math.cos(z * 0.009 - time * 0.55) * 9 + rippleHeight(x, z, time, waves) : 0;
    const altitudeOf = (intensity: number) => (settings.relief ? 85 + intensity * 190 : 50);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = css(palette.paper);
    ctx.fillRect(0, 0, width, height);
    const aura = ctx.createRadialGradient(width * 0.5, height * 0.52, 0, width * 0.5, height * 0.52, width * 0.58);
    aura.addColorStop(0, css(mix(palette.paper, palette.signalFill, 0.28)));
    aura.addColorStop(0.52, css(mix(palette.paper, palette.signalFill, 0.07)));
    aura.addColorStop(1, css(palette.paper));
    ctx.fillStyle = aura;
    ctx.fillRect(0, 0, width, height);

    // Ground grid and concentric instrument rings establish depth and scale.
    ctx.strokeStyle = css(mix(palette.paper, palette.signalFill, 0.35));
    ctx.lineWidth = 0.6;
    for (let n = -800; n <= 800; n += 80) {
      const a = project([n, -65, -700]),
        b = project([n, -65, 700]);
      const c = project([-800, -65, n]),
        d = project([800, -65, n]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.moveTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.stroke();
    }
    for (const radius of [590, 640, 710]) {
      ctx.beginPath();
      for (let i = 0; i <= 160; i++) {
        const angle = (i / 160) * Math.PI * 2;
        const p = project([Math.cos(angle) * radius, -62, Math.sin(angle) * radius * 0.73]);
        if (i) ctx.lineTo(p.x, p.y);
        else ctx.moveTo(p.x, p.y);
      }
      ctx.strokeStyle = css(radius === 640 ? palette.signalFill : mix(palette.paper, palette.signalFill, 0.45));
      ctx.stroke();
    }
    // Soft directional shadows underneath each floating prism.
    ctx.save();
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 18 * scale;
    ctx.fillStyle = "rgba(0,0,0,.55)";
    ctx.beginPath();
    for (const g of geometry) {
      const altitude = altitudeOf(g.intensity);
      const shadow = g.faces[0]!.map(([x, , z]) => project([x + altitude * 0.45, -60, z + altitude * 0.3]));
      shadow.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
    }
    ctx.fill();
    ctx.restore();
    // A luminous particle ribbon curls beneath the platform.
    ctx.fillStyle = css(palette.signalFill);
    for (let i = 0; i < 560; i++) {
      const a = i * 0.037 + time * 0.08;
      const r = 635 + Math.sin(i * 0.23 + time) * 20;
      const p = project([Math.cos(a) * r, -42 + Math.sin(a * 3 + time) * 20, Math.sin(a) * r * 0.73]);
      ctx.fillRect(p.x, p.y, i % 7 === 0 ? 2 : 1, i % 7 === 0 ? 2 : 1);
    }

    type Face = { polygon: Point[]; vertices: Vertex[]; g: (typeof geometry)[number]; top: boolean; depth: number };
    const faces: Face[] = [];
    for (const g of geometry) {
      const altitude = altitudeOf(g.intensity);
      const base = lift(g.cx, g.cz);
      g.faces.forEach((face, index) => {
        const vertices = face.map(([x, y, z]) => [x, base + y * altitude, z] as Vertex);
        const polygon = vertices.map(project);
        // Back faces cannot be seen or picked; cull before particle generation.
        const winding = polygon.reduce((sum, p, i) => {
          const next = polygon[(i + 1) % polygon.length]!;
          return sum + p.x * next.y - next.x * p.y;
        }, 0);
        if (winding <= 0) return;
        faces.push({ polygon, vertices, g, top: index === 0, depth: polygon.reduce((sum, p) => sum + p.depth, 0) / 4 });
      });
    }
    faces.sort((a, b) => a.depth - b.depth);
    hits = [];
    // Adaptive spacing keeps particle work bounded on narrow screens and zoom.
    const spacing = Math.max(10, 4.2 / scale);
    for (const face of faces) {
      const { g, top, vertices, polygon } = face;
      const id = g.block.token.id;
      const focused = id === settings.selected || id === hover;
      const tone = changeColor(g.block.token.change, palette);
      trace(polygon);
      const p0 = polygon[0]!,
        p2 = polygon[2]!;
      const shade = ctx.createLinearGradient(p0.x, p0.y, p2.x + 0.1, p2.y + 0.1);
      shade.addColorStop(0, css(mix(palette.paper, tone, top ? 0.32 : 0.2)));
      shade.addColorStop(1, css(mix(palette.paper, tone, top ? 0.16 : 0.08)));
      ctx.fillStyle = shade;
      ctx.fill();
      ctx.strokeStyle = css(focused ? palette.signal : mix(palette.paper, tone, top ? 0.7 : 0.4));
      ctx.lineWidth = focused ? 1.35 : 0.65;
      if (focused) {
        ctx.shadowColor = css(palette.signal);
        ctx.shadowBlur = 9;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
      const a = vertices[0]!,
        b = vertices[1]!,
        d = vertices[3]!;
      const distance = (p: Vertex, q: Vertex) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
      const cols = Math.max(1, Math.ceil(distance(a, b) / spacing));
      const lines = Math.max(1, Math.ceil(distance(a, d) / spacing));
      const brightness = top ? 0.62 + g.intensity * 0.3 : 0.38 + g.intensity * 0.22;
      ctx.fillStyle = css(focused ? mix(tone, palette.ink, 0.35) : mix(palette.paper, tone, Math.min(1, brightness)));
      ctx.beginPath();
      for (let u = 0; u <= cols; u++)
        for (let v = 0; v <= lines; v++) {
          const x = a[0] + ((b[0] - a[0]) * u) / cols + ((d[0] - a[0]) * v) / lines;
          const z = a[2] + ((b[2] - a[2]) * u) / cols + ((d[2] - a[2]) * v) / lines;
          let y = a[1] + ((b[1] - a[1]) * u) / cols + ((d[1] - a[1]) * v) / lines;
          // The top particle skin undulates; rigid prism faces retain clear bounds.
          if (top && settings.relief) y += rippleHeight(x, z, time, waves) * 0.75 + Math.sin(x * 0.025 + z * 0.013 + time * 1.3) * 3;
          const p = project([x, y, z]);
          const radius = top ? (focused ? 1.35 : 1.05) : 0.8;
          ctx.moveTo(p.x + radius, p.y);
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        }
      ctx.fill();
      hits.push({ polygon, block: g.block });
    }
    // Rising embers give the silhouette a live volumetric atmosphere.
    if (settings.relief) {
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      for (let i = 0; i < 90; i++) {
        const phase = (i * 0.618 + time * 0.065) % 1;
        const x = Math.sin(i * 127.1) * 530,
          z = Math.cos(i * 73.7) * 370;
        const p = project([x, 70 + phase * 390, z]);
        ctx.globalAlpha = Math.sin(phase * Math.PI) * 0.6;
        ctx.fillStyle = css(i % 4 ? palette.signalFill : palette.signal);
        ctx.fillRect(p.x, p.y, 1.5, i % 9 === 0 ? 6 : 1.5);
      }
      ctx.restore();
    }
    const labels: { x: number; y: number; w: number }[] = [];
    const labelOrder = [...geometry].sort(
      (a, b) => Number(b.block.token.id === settings.selected) - Number(a.block.token.id === settings.selected),
    );
    for (const g of labelOrder) {
      const id = g.block.token.id;
      const selected = id === settings.selected;
      const focused = selected || id === hover;
      if (!focused && Math.min(g.block.width, g.block.depth) * scale < 38) continue;
      const p = project([g.cx, lift(g.cx, g.cz) + altitudeOf(g.intensity) + 8, g.cz]);
      // Skip occluded labels, except the explicitly selected instrument.
      const covering = [...hits].reverse().find((hit) => contains(hit.polygon, p.x, p.y));
      if (!selected && covering && covering.block !== g.block) continue;
      ctx.font = `${focused ? 700 : 500} ${focused ? 14 : 11}px ui-monospace, monospace`;
      const w = ctx.measureText(g.block.token.symbol).width + 16;
      if (!focused && labels.some((l) => Math.abs(l.x - p.x) < (l.w + w) / 2 && Math.abs(l.y - p.y) < 28)) continue;
      labels.push({ x: p.x, y: p.y, w });
      ctx.fillStyle = css(palette.paper, 0.9);
      ctx.fillRect(p.x - w / 2, p.y - 12, w, 24);
      ctx.strokeStyle = css(focused ? palette.signal : palette.lineStrong);
      ctx.lineWidth = 0.7;
      ctx.strokeRect(p.x - w / 2, p.y - 12, w, 24);
      ctx.fillStyle = css(focused ? palette.ink : mix(palette.muted, palette.ink, 0.5));
      ctx.textAlign = "center";
      ctx.fillText(g.block.token.symbol, p.x, p.y + 4);
      // Labels are part of the click target, including floating selected labels.
      hits.push({
        block: g.block,
        polygon: [
          { x: p.x - w / 2, y: p.y - 12, depth: 0 },
          { x: p.x + w / 2, y: p.y - 12, depth: 0 },
          { x: p.x + w / 2, y: p.y + 12, depth: 0 },
          { x: p.x - w / 2, y: p.y + 12, depth: 0 },
        ],
      });
    }
  }

  function schedule(): void {
    if (!frame && !disposed && visible && !document.hidden) frame = requestAnimationFrame(tick);
  }
  function tick(now: number): void {
    frame = 0;
    if (disposed || !visible || document.hidden) {
      lastFrame = 0;
      return;
    }
    const elapsed = lastFrame ? now - lastFrame : 34;
    if (dirty || elapsed >= 32) {
      if (animated()) time += Math.min(elapsed, 64) / 1000;
      lastFrame = now;
      dirty = false;
      waves = waves.filter((wave) => time - wave.born <= 3);
      draw();
    }
    if (animated()) schedule();
  }
  function invalidate(): void {
    dirty = true;
    schedule();
  }
  const resize = new ResizeObserver(() => {
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    dpr = Math.min(devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    invalidate();
  });
  resize.observe(canvas);
  const intersection = new IntersectionObserver(([entry]) => {
    visible = entry?.isIntersecting ?? true;
    lastFrame = 0;
    invalidate();
  });
  intersection.observe(canvas);
  const wake = () => {
    lastFrame = 0;
    invalidate();
  };
  document.addEventListener("visibilitychange", wake);
  preference.addEventListener("change", wake);
  const hit = (x: number, y: number) => [...hits].reverse().find((entry) => contains(entry.polygon, x, y))?.block;
  return {
    /** New data: rebuild the prisms in place; the pointer and hit areas follow on the next frame. */
    setBlocks(next: readonly CapBlock<HeatTile>[]) {
      geometry = buildGeometry(next);
      if (hover && !next.some((block) => block.token.id === hover)) hover = "";
      invalidate();
    },
    update(next: SceneSettings) {
      settings = next;
      if (!animated()) waves = [];
      invalidate();
    },
    /** Hover at canvas coordinates; returns the tile under the pointer. */
    pointer(x: number, y: number): HeatTile | undefined {
      const block = hit(x, y);
      const id = block?.token.id ?? "";
      if (id !== hover) {
        hover = id;
        invalidate();
      }
      if (block && animated() && time - lastRipple > 0.12) {
        waves.push({ x: block.x + block.width / 2, z: block.z + block.depth / 2, born: time });
        waves = waves.slice(-6);
        lastRipple = time;
      }
      return block?.token;
    },
    pick(x: number, y: number) {
      return hit(x, y)?.token.id;
    },
    clearPointer() {
      hover = "";
      invalidate();
    },
    pulse() {
      if (animated()) {
        waves.push({ x: 0, z: 0, born: time });
        waves = waves.slice(-6);
        invalidate();
      }
    },
    dispose() {
      disposed = true;
      cancelAnimationFrame(frame);
      resize.disconnect();
      intersection.disconnect();
      document.removeEventListener("visibilitychange", wake);
      preference.removeEventListener("change", wake);
    },
  };
}
